import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Element, scroller } from 'react-scroll'
import { SpinnerAnimatedIcon } from 'secondstep-components'
import AdminSortButton from 'components/AdminSortButton'
import InterventionsSubheader from 'components/admin/Interventions/Subheader'
import SiteView from 'components/admin/Interventions/SiteView'
import TierLabel from 'components/TierLabel'
import {
  formatHoursSinceTimestamp,
  formatPageCount,
  formatSiteCount,
} from 'utils/reportsHelpers'
import {
  ORDER_BY,
  TIER2_DASHBOARD_SORT_KEYS,
} from 'store/reportsManager/constants'
import { getCountsAllSites } from './helpers'
import {
  HEADER_TEXT,
  LESSONS_COMPLETED_SORT_TITLE_TEXT,
  NEXT_BUTTON_TEXT,
  PREVIOUS_BUTTON_TEXT,
  SCHOOLS_SORT_TITLE_TEXT,
  SMALL_GROUPS_SORT_TITLE_TEXT,
  TIER_2_LABEL_TEXT,
} from './constants'
import {
  Bullet,
  ContentBodyWrapper,
  CountCopy,
  CountWrapper,
  HeaderWrapper,
  LastUpdatedAtText,
  LoadingWrapper,
  NameWrapper,
  NotCompletedCopy,
  PaginationButton,
  PaginationIndexContainer,
  PaginationPageWrapper,
  PaginationSiteWrapper,
  PanelWrapper,
  SiteCollapsible,
  SiteListContainer,
  SiteListFooter,
  SiteListHeader,
  StyledHeaderTwo,
  StyledHr,
  TierPill,
  TitleWrapper,
  Wrapper,
} from './component.styles'

const AdminK8Tier2MultiSiteView = ({
  currentPage,
  handlePaginationSortChange,
  isFetching,
  needsPagination,
  orderBy,
  siteEndCount,
  siteStartCount,
  sites,
  sortKey,
  totalItems,
  totalPages,
}) => {
  // Track which accordion is open - only one accordion can be open at a time
  // Using siteId to track the open accordion
  const [activeSiteId, setActiveSiteId] = useState(null)

  // Clear accordion state when sorting or pagination changes (but don't auto-open first)
  useEffect(() => {
    setActiveSiteId(null)
  }, [sortKey, orderBy, currentPage])

  // Scroll to content wrapper when pagination changes
  const scrollToContentWrapper = () => {
    scroller.scrollTo('content-wrapper', {
      duration: 200,
      delay: 50,
      smooth: true,
      offset: 60,
    })
  }

  const countMapAllSites = getCountsAllSites(sites)
  const formattedLastUpdatedAt = formatHoursSinceTimestamp(
    sites?.[0].lastUpdatedAt,
  )
  // Handler to toggle accordion open/closed - only one accordion open at a time
  const handleAccordionClick = siteId => {
    if (activeSiteId === siteId) {
      // If clicking the already-open accordion, close it
      setActiveSiteId(null)
    } else {
      // Otherwise, open the clicked accordion (closes any previously open one)
      setActiveSiteId(siteId)
    }
  }

  // Memoize sort buttons so they update when handlePaginationSortChange changes
  const sortButtons = useMemo(() => {
    // Guard: Don't build buttons if handlePaginationSortChange is not ready
    if (!handlePaginationSortChange) {
      return []
    }

    const onSortClick = clickedSortKey => {
      if (!handlePaginationSortChange) {
        return
      }

      const isSame = sortKey === clickedSortKey
      // For numeric sorts (Small Groups Created, Lessons Completed), default to ASC (smallest to largest)
      // For string sorts (Schools), default to ASC (alphabetical)
      const nextOrder = isSame
        ? orderBy === ORDER_BY.ASC
          ? ORDER_BY.DESC
          : ORDER_BY.ASC
        : ORDER_BY.ASC

      handlePaginationSortChange({
        newSortKey: clickedSortKey,
        newOrderBy: nextOrder,
        newPage: 1,
      })
      scrollToContentWrapper()
    }

    return [
      {
        sortKey: TIER2_DASHBOARD_SORT_KEYS.SCHOOLS,
        title: SCHOOLS_SORT_TITLE_TEXT,
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_DASHBOARD_SORT_KEYS.SMALL_GROUPS_CREATED,
        title: SMALL_GROUPS_SORT_TITLE_TEXT,
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_DASHBOARD_SORT_KEYS.LESSONS_COMPLETED,
        title: LESSONS_COMPLETED_SORT_TITLE_TEXT,
        handleOnClick: onSortClick,
      },
    ]
  }, [handlePaginationSortChange, sortKey, orderBy])

  if (isFetching) {
    return (
      <Wrapper>
        <Element name="content-wrapper" />
        <StyledHeaderTwo>{HEADER_TEXT}</StyledHeaderTwo>
        <StyledHr />
        <LoadingWrapper>
          <SpinnerAnimatedIcon />
          <p>Loading...</p>
        </LoadingWrapper>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <HeaderWrapper key="launch-key-1">
        <TitleWrapper>
          <StyledHeaderTwo>{HEADER_TEXT}</StyledHeaderTwo>
          <TierPill>
            <TierLabel labelText={TIER_2_LABEL_TEXT} />
          </TierPill>
        </TitleWrapper>
        <InterventionsSubheader
          countData={countMapAllSites}
          numberOfSites={sites?.length}
        />
      </HeaderWrapper>
      <LastUpdatedAtText>{formattedLastUpdatedAt}</LastUpdatedAtText>
      <StyledHr />
      <SiteListHeader>
        {sortButtons.map(button => (
          <AdminSortButton
            handleOnClick={() => button.handleOnClick(button.sortKey)}
            isActive={sortKey === button.sortKey}
            isCaretUp={sortKey === button.sortKey && orderBy === ORDER_BY.DESC}
            key={button.sortKey}
            title={button.title}
          />
        ))}
      </SiteListHeader>
      <SiteListContainer>
        {Array.isArray(sites) &&
          sites.map((site, index) => {
            return (
              <SiteCollapsible
                dataTestId={`site-collapsible-${site.siteId}`}
                followActiveProp={true}
                isActive={activeSiteId === site.siteId}
                key={`${site.siteId}-${index}`}
                onClick={() => handleAccordionClick(site.siteId)}
                title={
                  <PanelWrapper>
                    <NameWrapper>{site.schoolName}</NameWrapper>
                    <CountWrapper>
                      <CountCopy>{site.smallGroupsCreated}</CountCopy>
                      {site.groupsThatHaventCompletedLesson > 0 && (
                        <NotCompletedCopy>
                          <Bullet />
                          {`${site.groupsThatHaventCompletedLesson} group${
                            site.groupsThatHaventCompletedLesson === 1
                              ? " hasn't"
                              : "s haven't"
                          } completed a lesson`}
                        </NotCompletedCopy>
                      )}
                    </CountWrapper>
                    <CountWrapper>
                      <CountCopy>{site.lessonsCompleted}</CountCopy>
                    </CountWrapper>
                  </PanelWrapper>
                }
              >
                <ContentBodyWrapper>
                  <SiteView site={site} />
                </ContentBodyWrapper>
              </SiteCollapsible>
            )
          })}
      </SiteListContainer>
      <SiteListFooter>
        {needsPagination && (
          <PaginationButton
            disabled={currentPage <= 1}
            onClick={() => {
              handlePaginationSortChange({ newPage: currentPage - 1 })
              scrollToContentWrapper()
            }}
          >
            {PREVIOUS_BUTTON_TEXT}
          </PaginationButton>
        )}
        <PaginationIndexContainer>
          <PaginationPageWrapper>
            {formatPageCount(currentPage, totalPages)}
          </PaginationPageWrapper>
          <PaginationSiteWrapper>
            {formatSiteCount(siteStartCount, siteEndCount, totalItems)}
          </PaginationSiteWrapper>
        </PaginationIndexContainer>
        {needsPagination && (
          <PaginationButton
            disabled={currentPage >= totalPages}
            onClick={() => {
              handlePaginationSortChange({ newPage: currentPage + 1 })
              scrollToContentWrapper()
            }}
          >
            {NEXT_BUTTON_TEXT}
          </PaginationButton>
        )}
      </SiteListFooter>
    </Wrapper>
  )
}

AdminK8Tier2MultiSiteView.propTypes = {
  currentPage: PropTypes.number,
  handlePaginationSortChange: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  needsPagination: PropTypes.bool,
  orderBy: PropTypes.string,
  siteEndCount: PropTypes.number,
  siteStartCount: PropTypes.number,
  sites: PropTypes.array.isRequired,
  sortKey: PropTypes.string,
  totalItems: PropTypes.number,
  totalPages: PropTypes.number,
}

export default AdminK8Tier2MultiSiteView
